import { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";

import { useDialog } from "@datanac/datanac-mui-toolkit";
import { EastOutlined } from "@mui/icons-material";
import ArrowDropDownOutlined from "@mui/icons-material/ArrowDropDownOutlined";
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import Button from "@mui/material/Button";
import { UsersApiHelper } from "api/ApiHelper";
import { AppContext } from "App";
import { BudgetService } from "components/Budget/BudgetService";
import { getBudgetYears } from "components/Budget/helpers";
import TilleyDataGrid from "components/shared/DataGrid/TilleyDataGrid";
import WorkspaceActionBar from "components/Workspaces/WorkspaceActionBar";
import WorkspaceHeader from "components/Workspaces/WorkspaceHeader";
import PopupState, { bindMenu, bindTrigger } from "material-ui-popup-state";
import { useSnackbar } from "notistack";
import { formatNumber, formatPracticeName, formatPrice } from "utility/formatters";
import { v4 as uuidv4 } from "uuid";

import { LoanOriginationIcon } from "../../Icons/Icons";

export default function LoanBookListWorkspace() {
  const { globalState } = useContext(AppContext);
  const navigate = useNavigate();

  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("lg"), { noSsr: true });
  const { enqueueSnackbar } = useSnackbar();

  const { component: confirmDialogComponent } = useDialog();

  const [currentYear, setCurrentYear] = useState(globalState?.crop_year);

  const handleError = (err) => {
    console.warn(err);
    enqueueSnackbar(err.detail || err.message || JSON.stringify(err), { preventDuplicate: true, variant: "error" });
  };

  const [isLoadingUserGrants, setIsLoadingUserGrants] = useState(true);
  const [userGrants, setUserGrants] = useState([]);
  const [userGrantsTimestamp, setUserGrantsTimestamp] = useState(uuidv4());
  const loadUserGrants = async () => {
    try {
      const _userGrants = await UsersApiHelper.selectUserGrants(
        { user_id: globalState.agent_token },
        { user_role: globalState?.user_role, agent_token: globalState?.agent_token }
      );
      setUserGrants(_userGrants);
      setUserGrantsTimestamp(uuidv4());
      setIsLoadingUserGrants(false);
    } catch (err) {
      setIsLoadingUserGrants(false);
      setUserGrants([]);
      setUserGrantsTimestamp(uuidv4());
      handleError(err);
    }
  };

  const calculateActiveUserGrants = () => {
    if (userGrants?.length) {
      return userGrants.filter((ug) => ug.is_active);
    } else {
      return [];
    }
  };

  useEffect(() => {
    loadUserGrants();
  }, [currentYear]);

  // ---

  useEffect(() => {
    const loadBudgets = async () => {
      const activeProducers = calculateActiveUserGrants();
      for (var producer of activeProducers) {
        const { actions: budgetActions } = new BudgetService({
          apiConfig: { ...globalState, producer_token: producer.producer_token },
          year: currentYear,
        });

        // TODO: async
        producer.budget = (await budgetActions.getBudget()) || {};
      }

      setUserGrants(activeProducers);
    };

    loadBudgets();
  }, [userGrantsTimestamp]);

  // ---

  const columns = useMemo(
    () => [
      {
        field: "producer_description",
        headerName: "Producer",
        flex: 2,
      },
      {
        field: "producer_email",
        headerName: "Email",
        flex: 2,
      },
      {
        field: "reported_acreage",
        headerName: "Acres",
        valueFormatter: (value, row) => formatNumber(row?.budget?.reported_acreage),
        renderCell: (params) => {
          return <Box sx={{ textAlign: "right", width: "100%" }}>{params.value}</Box>;
        },
        flex: 0,
      },
      {
        field: "expenses_total",
        headerName: "Loan Request",
        renderCell: (params) => {
          const currentProducer = params.row;
          return (
            <Box sx={{ textAlign: "right", width: "100%" }}>
              {currentProducer?.budget?.expenses?.total ? formatPrice(currentProducer?.budget?.expenses?.total) : "--"}
            </Box>
          );
        },
        flex: 1,
      },
      {
        field: "CropProfiles",
        headerName: "Crop Profiles",
        display: "flex",
        renderCell: (params) => {
          const currentProducer = params.row;
          return (
            <Box alignItems="left" sx={{ width: "100%" }}>
              {!currentProducer?.budget && <CircularProgress size="1rem" />}
              <Stack direction="row" spacing={1} sx={{ display: "flex", flexWrap: "wrap", gap: 1, p: 2 }}>
                {currentProducer?.budget?.budgetCommodities?.map((currentCropProfile, index) => (
                  <Chip
                    key={index}
                    label={
                      currentCropProfile?.commodity_name + " " + formatPracticeName(currentCropProfile?.practice_name)
                    }
                  />
                ))}
              </Stack>
            </Box>
          );
        },
        flex: 2,
      },
      {
        field: "producer_token",
        headerName: "",
        renderCell: (params) => {
          return (
            <Box display="flex" justifyContent="flex-end">
              <IconButton
                onClick={() => {
                  navigate(`/lender/loan_book/loan_origination_memo/${params?.row?.producer_token}/${currentYear}`);
                }}
              >
                <EastOutlined />
              </IconButton>
            </Box>
          );
        },
        flex: 0,
      },
    ],
    [navigate]
  );

  // --- --- ---

  return (
    <>
      <WorkspaceHeader
        title="Loan Book"
        category={{
          title: "Loan Origination",
          icon: <LoanOriginationIcon />,
        }}
      />

      <Stack direction="row" spacing={2} alignItems="center" className="headerWithMenuButton navigation">
        <Typography variant="h1">Year</Typography>
        <PopupState variant="popover" popupId="demo-popup-menu">
          {(popupState) => (
            <>
              <Button variant="outlined" {...bindTrigger(popupState)}>
                {currentYear} <ArrowDropDownOutlined />
              </Button>
              <Menu {...bindMenu(popupState)}>
                {getBudgetYears().map((year, index) => (
                  <MenuItem
                    key={index}
                    onClick={() => {
                      setCurrentYear(year);
                      popupState.close();
                    }}
                  >
                    {year}
                  </MenuItem>
                ))}
              </Menu>
            </>
          )}
        </PopupState>
        {isLoadingUserGrants && <CircularProgress />}
      </Stack>

      {Boolean(calculateActiveUserGrants()?.length) && (
        <>
          <TilleyDataGrid
            rows={calculateActiveUserGrants()}
            columns={columns}
            pageSizeOptions={[5, 10, 25]}
            defaultPageSize={isMobile ? 5 : 10}
            gridKey="loan-book-list"
            sx={{ width: "99%", boxSizing: "border-box" }}
            getRowId={(row) => row.id}
            isLoading={isLoadingUserGrants}
          />
        </>
      )}

      <WorkspaceActionBar content={<></>} />

      {confirmDialogComponent}
    </>
  );
}
